import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, ExternalLink, Landmark } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { payoutView, countryName, countryOptions, needsCountry } from '@/utils/payoutAccount';
import VerificationGate from '@/components/verification/VerificationGate';
import { canDo } from '@/utils/verifications';
import '@/styles/subscription.css';
import '@/styles/payouts.css';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;

const ICONS = { pro: CheckCircle2, warn: AlertTriangle, off: Clock };

export default function PayoutsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const isCarrier = (user?.roles || []).includes('ROLE_CARRIER');

  const [account, setAccount] = useState(null);
  const [error, setError] = useState('');
  const [country, setCountry] = useState('');
  const [busy, setBusy] = useState(false);
  const handledReturn = useRef(false);

  useEffect(() => {
    if (!isCarrier) return undefined;
    let cancel = false;
    const back = new URLSearchParams(location.search).get('onboarding');
    (async () => {
      try {
        // Au retour de Stripe, l'état est relu dans Stripe : la page de retour ne prouve rien
        const { data } = back
          ? await axios.post(`${API}/payouts/account/sync`)
          : await axios.get(`${API}/payouts/account`);
        if (cancel) return;
        setAccount(data);
        if (back && !handledReturn.current) {
          handledReturn.current = true;
          if (data.status === 'READY') toast.success('Votre compte de versement est prêt.');
          else if (data.status === 'IN_REVIEW') toast.info('Informations envoyées : Stripe les vérifie.');
          else toast.info('La configuration n\'est pas terminée : vous pouvez la reprendre quand vous voulez.');
          navigate('/app/versements', { replace: true });
        }
      } catch {
        if (!cancel) setError('Impossible de charger votre compte de versement. Réessayez dans un instant.');
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCarrier]);

  const open = async (action) => {
    setBusy(true);
    try {
      const body = action === 'onboarding'
        ? { origin_url: window.location.origin, ...(needsCountry(account) ? { country } : {}) }
        : undefined;
      const { data } = await axios.post(`${API}/payouts/account/${action}`, body);
      window.location.assign(data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible d\'ouvrir la page de Stripe.');
      setBusy(false);
    }
  };

  const header = (
    <div className="c2s-topbar">
      <div className="c2s-topbar-title">Mes versements</div>
      <div className="c2s-topbar-sub">Le compte bancaire sur lequel vous recevez vos gains</div>
    </div>
  );

  if (!isCarrier) {
    return (
      <div data-testid="payout-page">
        {header}
        <div className="card sub-carrier-only" data-testid="payout-carrier-only">
          Les versements concernent les transporteurs : ce sont eux qui reçoivent le prix des missions.
          En tant qu'expéditeur, vous n'avez rien à configurer ici.
        </div>
      </div>
    );
  }

  if (error) {
    return <div data-testid="payout-page">{header}<div className="card sub-error">{error}</div></div>;
  }

  if (!account) {
    return <div data-testid="payout-page">{header}<div className="skeleton" style={{ height: 220 }} /></div>;
  }

  const view = payoutView(account.status);
  const Icon = ICONS[view.tone];
  const askCountry = needsCountry(account);
  const options = countryOptions(account.countries);

  return (
    <div data-testid="payout-page">
      {header}

      <div className={`sub-status sub-status--${view.tone}`} data-testid="payout-status" data-status={account.status}>
        <Icon size={20} aria-hidden="true" />
        <div className="sub-status-body">
          <div className="sub-status-title">{view.title}</div>
          <div className="sub-status-text">
            {view.text}
            {account.country && ` Compte bancaire : ${countryName(account.country)}.`}
          </div>
        </div>
      </div>

      <section className="card pay-card" aria-labelledby="pay-card-title">
        <h2 id="pay-card-title" className="card-title"><Landmark size={16} aria-hidden="true" /> Compte de versement</h2>

        {!account.stripe_enabled && (
          <p className="sub-note" data-testid="payout-unavailable">
            La configuration des versements est indisponible pour le moment.
          </p>
        )}

        {account.stripe_enabled && askCountry && (
          <div className="pay-field">
            <label htmlFor="pay-country" className="pay-label">Pays de votre compte bancaire</label>
            <select
              id="pay-country"
              className="pay-select"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="payout-country"
            >
              <option value="">Choisir un pays…</option>
              {options.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
            </select>
            <p className="sub-note sub-note--muted" data-testid="payout-country-note">
              Votre pays n'est pas dans la liste ? Les versements n'y sont pas encore disponibles :
              vos gains restent en sécurité sur Colis2Ship en attendant. Le pays ne peut plus être changé une fois le compte créé.
            </p>
          </div>
        )}

        {/* Ouvrir son compte de versement exige les mêmes vérifications que publier (verifications.py) */}
        {account.stripe_enabled && view.action === 'onboarding' && !canDo(user, 'receive_payouts') && (
          <VerificationGate user={user} action="receive_payouts" variant="panel" />
        )}
        {account.stripe_enabled && view.action && (view.action !== 'onboarding' || canDo(user, 'receive_payouts')) && (
          <button
            type="button"
            className="btn btn-primary btn-lg sub-cta"
            onClick={() => open(view.action)}
            disabled={busy || (askCountry && !country)}
            data-testid="payout-cta"
          >
            <ExternalLink size={16} aria-hidden="true" />
            {view.actionLabel}
          </button>
        )}

        <p className="sub-note">
          Vos coordonnées bancaires et vos pièces d'identité sont saisies chez Stripe et ne passent jamais par Colis2Ship.
          Les versements se font en euros.
        </p>
      </section>
    </div>
  );
}
